import React, { useState, useEffect } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Toolbar,
  Chip,
  Badge,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Dashboard,
  Assignment,
  AssignmentTurnedIn,
  Business,
  Badge as BadgeIcon,
  Assessment,
  People,
  Person,
  Checklist,
  AccountTree,
  DateRange,
  Grading,
  Calculate,
  Psychology,
  BarChart,
  WorkOutline,
  School,
  ChevronLeft,
  ChevronRight,
  PriceChange,
} from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import { salesApprovalsApi } from '../../services/salesApi'
import {
  DASHBOARD_PATH,
  SELF_EVALUATION_PATH,
  GOAL_SETTING_PATH,
  GOAL_APPROVAL_PATH,
  getManagerEvaluationListPath,
  getDeptHeadEvaluationListPath,
  REPORTS_PATH,
  HR_EVALUATIONS_PATH,
  ADMIN_PATHS,
  PROFILE_PATH,
  SKILL_PATHS,
  PROJECT_PATHS,
  CERTIFICATION_PATHS,
  SALES_PATHS,
} from '../../utils/routes'

interface SidebarProps {
  onItemClick?: () => void
  collapsed?: boolean
  onToggleCollapse?: () => void
}

interface MenuItem {
  id: string
  label: string
  path: string
  icon: React.ReactElement
  roles?: string[]
  badge?: string
  badgeColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
  isDivider?: boolean
}

const Sidebar: React.FC<SidebarProps> = ({ onItemClick, collapsed = false, onToggleCollapse }) => {
  const { user, evaluatorInfo, periodStatus, skillPermissions, salesPermissions } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)

  // 現在アクティブな評価期間IDを取得
  const activePeriodId = periodStatus?.activePeriodId || ''

  // 承認待ち件数を取得
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (salesPermissions?.can_manage_cases) {
        try {
          const response = await salesApprovalsApi.getPendingCount()
          if (response.data.success) {
            setPendingApprovalCount(response.data.data.count)
          }
        } catch (err) {
          console.error('承認待ち件数取得エラー:', err)
        }
      }
    }
    fetchPendingCount()
    // 30秒ごとに更新
    const interval = setInterval(fetchPendingCount, 30000)
    return () => clearInterval(interval)
  }, [salesPermissions])

  const menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'ダッシュボード',
      path: DASHBOARD_PATH,
      icon: <Dashboard />,
    },
    {
      id: 'my-skills',
      label: 'マイスキル',
      path: SKILL_PATHS.mySkills,
      icon: <Psychology />,
    },
    {
      id: 'goal-setting',
      label: '目標設定',
      path: GOAL_SETTING_PATH,
      icon: <Assignment />,
    },
    {
      id: 'goal-approval',
      label: '目標承認',
      path: GOAL_APPROVAL_PATH,
      icon: <Checklist />,
    },
    {
      id: 'self-evaluation',
      label: '自己評価',
      path: SELF_EVALUATION_PATH,
      icon: <Assignment />,
    },
    {
      id: 'skill-approval',
      label: 'スキル承認',
      path: SKILL_PATHS.approval,
      icon: <Checklist />,
    },
    {
      id: 'manager-evaluation',
      label: '上長評価',
      path: getManagerEvaluationListPath(activePeriodId),
      icon: <AssignmentTurnedIn />,
    },
    {
      id: 'dept-evaluation',
      label: '部門長評価',
      path: getDeptHeadEvaluationListPath(activePeriodId),
      icon: <Business />,
    },
    {
      id: 'reports',
      label: 'レポート',
      path: REPORTS_PATH,
      icon: <Assessment />,
    },
    {
      id: 'hr-evaluation-management',
      label: 'HR評価管理',
      path: HR_EVALUATIONS_PATH,
      icon: <Grading />,
      roles: ['hr'],
    },
  ]

  const adminMenuItems: MenuItem[] = [
    {
      id: 'user-management',
      label: 'ユーザー管理',
      path: ADMIN_PATHS.users,
      icon: <People />,
      roles: ['hr', 'system_supervisor'],
    },
    {
      id: 'department-management',
      label: '部門管理',
      path: ADMIN_PATHS.departments,
      icon: <Business />,
      roles: ['hr'],
    },
    {
      id: 'rank-cost-management',
      label: 'ランクコスト管理',
      path: ADMIN_PATHS.rankCost,
      icon: <PriceChange />,
      roles: ['hr'],
    },
    {
      id: 'position-management',
      label: '所属・職位管理',
      path: ADMIN_PATHS.positions,
      icon: <Business />,
      roles: ['hr'],
    },
    {
      id: 'job-title-management',
      label: '役職マスタ管理',
      path: ADMIN_PATHS.jobTitles,
      icon: <BadgeIcon />,
      roles: ['hr'],
    },
    {
      id: 'divider-2',
      label: '',
      path: '',
      icon: <></>,
      isDivider: true,
    },
    {
      id: 'evaluation-periods-management',
      label: '評価期間管理',
      path: ADMIN_PATHS.evaluationPeriods,
      icon: <DateRange />,
      roles: ['hr'],
    },
    {
      id: 'evaluation-items-management',
      label: '評価項目マスタ管理',
      path: ADMIN_PATHS.evaluationItems,
      icon: <Checklist />,
      roles: ['hr'],
    },
    {
      id: 'evaluation-routes-management',
      label: '評価ルート設定',
      path: ADMIN_PATHS.evaluationRoutes,
      icon: <AccountTree />,
      roles: ['hr'],
    },
    {
      id: 'r-value-management',
      label: 'R値手入力・確定',
      path: ADMIN_PATHS.rValue,
      icon: <Calculate />,
      roles: ['hr'],
    },
    {
      id: 'divider-3',
      label: '',
      path: '',
      icon: <></>,
      isDivider: true,
    },
    {
      id: 'functional-permissions',
      label: '機能権限指定',
      path: ADMIN_PATHS.permissions,
      icon: <AssignmentTurnedIn />,
      roles: ['skill_supervisor', 'sales_supervisor'],
    },
    {
      id: 'audit-logs',
      label: '監査ログ',
      path: ADMIN_PATHS.auditLogs,
      icon: <Assessment />,
      roles: ['system_supervisor'],
    },
  ]

  const skillMenuItems: MenuItem[] = [
    {
      id: 'skill-master-management',
      label: 'スキルマスタ管理',
      path: ADMIN_PATHS.skillMaster,
      icon: <Psychology />,
    },
  ]

  const salesMenuItems: MenuItem[] = [
    {
      id: 'sales-cases',
      label: '案件一覧',
      path: SALES_PATHS.cases,
      icon: <WorkOutline />,
    },
    {
      id: 'sales-kpi',
      label: 'KPIダッシュボード',
      path: SALES_PATHS.kpi,
      icon: pendingApprovalCount > 0 ? (
        <Badge badgeContent={pendingApprovalCount} color="error" max={99}>
          <BarChart />
        </Badge>
      ) : (
        <BarChart />
      ),
    },
    {
      id: 'sales-snapshots',
      label: '計画スナップショット',
      path: SALES_PATHS.snapshots,
      icon: <Assessment />,
    },
    {
      id: 'sales-accounts',
      label: '取引先マスタ',
      path: SALES_PATHS.accounts,
      icon: <Business />,
    },
  ]

  const personalMenuItems: MenuItem[] = [
    {
      id: 'profile',
      label: 'プロフィール',
      path: PROFILE_PATH,
      icon: <Person />,
    },
  ]

  const handleItemClick = (path: string) => {
    navigate(path)
    onItemClick?.()
  }

  const hasRole = (roles?: string[]) => {
    if (!roles || roles.length === 0) return true
    if (!user?.roles) return false
    return roles.some(role => user.roles.includes(role))
  }

  // employee ロールチェック（システム全機能へのアクセスに必須）
  const hasEmployeeRole = () => {
    return user?.roles?.includes('employee') || false
  }

  // スキル管理権限チェック
  const hasSkillMasterEditPermission = () => {
    return skillPermissions.some(p => p.permission_type === 'skill_master_editor')
  }

  const hasSkillMapViewPermission = () => {
    return skillPermissions.some(p => p.permission_type === 'skill_map_viewer')
  }

  // 営業管理権限チェック
  const hasSalesPermission = (permissionType: string) => {
    if (!salesPermissions) return false
    switch (permissionType) {
      case 'manage_accounts':
        return salesPermissions.can_manage_accounts
      case 'manage_cases':
        return salesPermissions.can_manage_cases
      case 'view_cases':
        return salesPermissions.can_view_cases || salesPermissions.can_manage_cases
      case 'view_dashboard':
        return salesPermissions.can_view_dashboard
      case 'view_kpi':
        return salesPermissions.can_view_kpi
      default:
        return false
    }
  }

  const renderMenuItem = (item: MenuItem) => {
    // Divider check - render divider if isDivider is true
    if (item.isDivider) {
      return <Divider key={item.id} sx={{ my: 1, mx: 2 }} />
    }

    // Menu visibility control based on evaluatorInfo and periodStatus
    // 評価期間がdraft状態または存在しない場合は、全ての評価メニューを非表示
    const hasActivePeriod = periodStatus?.hasActivePeriod ?? false

    // マイスキル: 技術部門のみ表示
    if (item.id === 'my-skills') {
      if (user?.department_type !== 'tech') return null
    }
    if (item.id === 'goal-setting') {
      if (!evaluatorInfo?.is_self_evaluator || !hasActivePeriod) return null
      // 承認済み（目標確定）は編集不可のためメニューを非表示
      if (periodStatus?.goalApprovalStatus === 'approved') return null
    }
    if (item.id === 'goal-approval') {
      if (!evaluatorInfo?.is_goal_approver_evaluator || !hasActivePeriod) return null
    }
    // 自己評価: 評価ルートに設定されている場合かつアクティブな評価期間がある場合のみ表示
    if (item.id === 'self-evaluation') {
      if (!evaluatorInfo?.is_self_evaluator || !hasActivePeriod) return null
    }
    // スキル承認: 部門長として設定されている場合かつ技術部門のみ表示
    if (item.id === 'skill-approval') {
      if (!user?.is_department_manager || user?.department_type !== 'tech') return null
    }
    // 上長評価: 評価ルートに設定されている場合かつアクティブな評価期間がある場合のみ表示
    if (item.id === 'manager-evaluation') {
      if (!evaluatorInfo?.is_manager_evaluator || !hasActivePeriod) return null
    }
    // 部門長評価: 評価ルートに設定されている場合かつアクティブな評価期間がある場合のみ表示
    if (item.id === 'dept-evaluation') {
      if (!evaluatorInfo?.is_dept_head_evaluator || !hasActivePeriod) return null
    }

    // スキル管理: スキルマスタ編集権限が必要
    if (item.id === 'skill-master-management') {
      if (!hasSkillMasterEditPermission()) return null
    }

    // 営業管理: 各メニューに対応する権限が必要
    if (item.id === 'sales-cases') {
      if (!hasSalesPermission('view_cases') && !hasSalesPermission('manage_cases')) return null
    }
    if (item.id === 'sales-budget') {
      if (!hasSalesPermission('view_cases') && !hasSalesPermission('manage_cases')) return null
    }
    if (item.id === 'sales-kpi') {
      if (!hasSalesPermission('view_kpi')) return null
    }
    if (item.id === 'sales-snapshots') {
      if (!hasSalesPermission('manage_accounts')) return null
    }
    if (item.id === 'sales-accounts') {
      if (!hasSalesPermission('manage_accounts')) return null
    }

    // Admin menus still use role-based control
    if (item.roles && !hasRole(item.roles)) return null

    const isSelected = location.pathname === item.path

    const button = (
      <ListItem disablePadding>
        <ListItemButton
          selected={isSelected}
          onClick={() => handleItemClick(item.path)}
          sx={{
            borderRadius: 1,
            mx: collapsed ? 0.5 : 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 1 : 2,
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '& .MuiListItemIcon-root': {
                color: 'primary.contrastText',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: isSelected ? 'inherit' : 'action.active',
              minWidth: collapsed ? 0 : 40,
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
          {!collapsed && (
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isSelected ? 600 : 400,
              }}
            />
          )}
          {!collapsed && item.badge && (
            <Chip
              label={item.badge}
              size="small"
              color={item.badgeColor || 'primary'}
              sx={{ ml: 1 }}
            />
          )}
        </ListItemButton>
      </ListItem>
    )

    if (collapsed) {
      return (
        <Tooltip key={item.id} title={item.label} placement="right" arrow>
          {button}
        </Tooltip>
      )
    }
    return <React.Fragment key={item.id}>{button}</React.Fragment>
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: collapsed ? 1 : 2 }}>
        <Box sx={{ flex: 1, py: 1, display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          {collapsed ? (
            <img src="/logo.svg" alt="" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
          ) : (
            <img src="/logo.svg" alt="TGS社内管理システム" style={{ maxWidth: '180px', maxHeight: '48px', objectFit: 'contain' }} />
          )}
        </Box>
        {onToggleCollapse && !collapsed && (
          <IconButton onClick={onToggleCollapse} sx={{ width: 36, height: 36 }}>
            <ChevronLeft sx={{ fontSize: 28 }} />
          </IconButton>
        )}
        {onToggleCollapse && collapsed && (
          <IconButton onClick={onToggleCollapse} sx={{ position: 'absolute', right: 4 }}>
            <ChevronRight />
          </IconButton>
        )}
      </Toolbar>

      <Divider />

      {/* ユーザー情報 */}
      {!collapsed && (
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.employee_id} | {user?.department}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {user?.roles?.map((role) => (
              <Chip
                key={role}
                label={getRoleLabel(role)}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            )) || (
              <Chip
                label={getRoleLabel(user?.role || 'employee')}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </Box>
      )}

      <Divider />

      {/* メインメニュー（employee ロール必須） */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {hasEmployeeRole() && (
          <List sx={{ px: 1, py: 2 }}>
            {menuItems.map(renderMenuItem)}
          </List>
        )}

        {/* 管理機能 */}
        {(user?.roles?.includes('hr') || user?.roles?.includes('system_supervisor') ||
          user?.roles?.includes('skill_supervisor') || user?.roles?.includes('sales_supervisor') ||
          user?.role === 'hr') && (
          <>
            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 1,
                  pt: 2,
                  display: 'block',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                管理機能
              </Typography>
            )}
            {collapsed && <Divider sx={{ my: 1 }} />}
            <List sx={{ px: 1, py: 0 }}>
              {adminMenuItems.map(renderMenuItem)}
            </List>
          </>
        )}

        {/* スキル管理 */}
        {hasSkillMasterEditPermission() && (
          <>
            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 1,
                  pt: 2,
                  display: 'block',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                スキル管理
              </Typography>
            )}
            {collapsed && <Divider sx={{ my: 1 }} />}
            <List sx={{ px: 1, py: 0 }}>
              {skillMenuItems.map(renderMenuItem)}
            </List>
          </>
        )}

        {/* 営業管理 */}
        {(hasSalesPermission('manage_accounts') || hasSalesPermission('manage_cases') ||
          hasSalesPermission('view_cases') || hasSalesPermission('view_dashboard') ||
          hasSalesPermission('view_kpi')) && (
          <>
            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  py: 1,
                  pt: 2,
                  display: 'block',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                営業管理
              </Typography>
            )}
            {collapsed && <Divider sx={{ my: 1 }} />}
            <List sx={{ px: 1, py: 0 }}>
              {salesMenuItems.map(renderMenuItem)}
            </List>
          </>
        )}

        {/* 個人設定 */}
        {!collapsed && (
          <Typography
            variant="overline"
            sx={{
              px: 2,
              py: 1,
              display: 'block',
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            個人設定
          </Typography>
        )}
        {collapsed && <Divider sx={{ my: 1 }} />}
        <List sx={{ px: 1, py: 0 }}>
          {personalMenuItems.map(renderMenuItem)}
        </List>
      </Box>
    </Box>
  )
}

// ロール名の日本語変換
const getRoleLabel = (role: string): string => {
  const roleLabels: { [key: string]: string } = {
    system_supervisor: 'システム管理者',
    hr: '人事',
    skill_supervisor: 'スキル管理者',
    sales_supervisor: '営業管理者',
    employee: '社員',
    // 旧ロール（後方互換性のため残す）
    manager: '上長',
    dept_head: '部門長',
    sales: '営業',
  }
  return roleLabels[role] || role
}

export default Sidebar